/**
 * Serialization boundary (RFC 0215): in-memory model <-> Searugast on-doc shape.
 *
 * The in-memory `LogDoc` (used by projection/coordination) is idiomatic: bare
 * string enums, JS-number timestamps, the log at the root. The ON-DOC shape, the
 * canonical one both peers must write byte-identically, is Searugast's automorph
 * representation:
 *   - the log nests under the `thread` key;
 *   - unit enums (`role`, `status`) are single-key objects `{ role: "..." }`;
 *   - integers are Automerge Int/Uint (i64 createdAt, u32 schemaVersion,
 *     usize stepIndex), and read back as BigInt in JS.
 * This module is the ONLY place that crosses that boundary. The conformance
 * round-trip tests (mod-core writes, Searugast reads, and vice versa) are the
 * oracle that keeps it honest.
 */

import { Automerge } from '@automerge/automerge-repo/slim'
// RFC 0258: the on-doc root key (whiffletree). DEFINED in ./thread-key (a
// dependency-free module) and re-exported here so codec's importers are
// unchanged — bootstrap.ts must import it WITHOUT pulling codec's fullfat
// `@automerge/automerge` into the SharedWorker graph. See thread-key.ts.
import { THREAD_KEY } from './thread-key'
import type { Entry, EntryId, LogDoc, NewEntry, TimestampMs } from './types'

export { THREAD_KEY } from './thread-key'

const ENUM_FIELDS = new Set(['role', 'status'])
const INT_FIELDS = new Set(['startedAt', 'completedAt'])
const UINT_FIELDS = new Set(['stepIndex', 'version'])

// ---------------------------------------------------------------------------
// encode (in-memory -> on-doc), for writing into an Automerge change draft
// ---------------------------------------------------------------------------

/** A scalar (immutable) string, matching Searugast's String fields. */
function rs(v: string): unknown {
  return new Automerge.ImmutableString(v)
}

/** Deep-encode an arbitrary JSON value (e.g. tool arguments) so inner strings
 * are scalar, matching how automorph stores a serde_json::Value. */
function encodeJson(v: unknown): unknown {
  if (typeof v === 'string') return rs(v)
  if (Array.isArray(v)) return v.map(encodeJson)
  if (v !== null && typeof v === 'object') {
    const o: Record<string, unknown> = {}
    for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
      o[k] = encodeJson(val)
    }
    return o
  }
  return v
}

const STRING_ARRAY_FIELDS = new Set(['inputHeads', 'filesAffected'])

/**
 * Build the data object. Strings become RawString scalars (matching automorph's
 * String fields). `text` too, since RFC 0317: nothing on a session log is typed
 * into by two writers at once, and a collaborative Text costs one op per
 * character, held in wasm memory for the life of the process. Sessions written
 * before 0317 still carry a Text there, and `decodeValue` reads both. Enums wrap
 * as `{ role: RawString }`. Int fields are EXCLUDED and direct-assigned later,
 * because Automerge only honours Int/Uint on direct assignment, not nested.
 * RawString, by contrast, survives nesting, so it is used in literals here.
 */
export function encodeData(
  data: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(data)) {
    if (v === undefined || v === null) continue // Option None: field absent
    if (INT_FIELDS.has(k) || UINT_FIELDS.has(k)) continue // direct-assign, after
    if (ENUM_FIELDS.has(k) && typeof v === 'string') out[k] = { [k]: rs(v) }
    else if (typeof v === 'string') out[k] = rs(v)
    else if (k === 'toolUses' && Array.isArray(v)) out[k] = v.map(encodeToolUse)
    else if (STRING_ARRAY_FIELDS.has(k) && Array.isArray(v))
      out[k] = v.map((x) => rs(String(x)))
    else if (typeof v === 'object')
      out[k] = encodeJson(v) // metadata, usage, model
    else out[k] = v // booleans, numbers
  }
  return out
}

export function encodeToolUse(t: unknown): Record<string, unknown> {
  const tu = t as { id: unknown; name: unknown; arguments: unknown }
  return {
    id: rs(String(tu.id)),
    name: rs(String(tu.name)),
    arguments: encodeJson(tu.arguments),
  }
}

/** Initialize an empty log under the `thread` key (direct-assign the int). */
export function initThread(d: Record<string, unknown>): void {
  d[THREAD_KEY] = { entries: {}, toolWorkers: {} }
  ;(d[THREAD_KEY] as Record<string, unknown>).schemaVersion =
    new Automerge.Uint(1)
}

/** Write an entry into `thread.entries[id]`, setting int scalars directly. */
export function writeEntry(
  thread: Record<string, unknown>,
  id: EntryId,
  parents: EntryId[],
  createdAt: TimestampMs,
  payload: NewEntry,
): void {
  const rawData = payload.payload as unknown as Record<string, unknown>
  // RFC 0258: whiffletree's `MessageEvent.metadata` is REQUIRED (non-Option) — its
  // reader rejects a message entry without it, and one bad entry makes the WHOLE
  // session unreadable to a whiffletree reader (iOS). Every message writer must emit
  // it: the model builder (`messagePayload`) does, but the run worker appends
  // `{ type: 'message', … }` inline via `BranchLog`. Default it here at the single
  // write chokepoint so no writer can omit it.
  const data =
    payload.type === 'message' && rawData.metadata === undefined
      ? { ...rawData, metadata: {} }
      : rawData
  const entries = thread.entries as Record<string, Record<string, unknown>>
  entries[id] = {
    id: rs(id),
    parents: parents.map((p) => rs(p)),
    // RFC 0258: entry envelope key is whiffletree's `body` (was `payload`); the
    // inner content key stays `payload` (automorph content = "payload").
    body: { type: rs(payload.type), payload: encodeData(data) },
  }
  const entry = entries[id]
  entry.createdAt = new Automerge.Int(createdAt)
  const target = (entry.body as { payload: Record<string, unknown> }).payload
  for (const [k, v] of Object.entries(data)) {
    if (v === undefined || v === null) continue
    if (UINT_FIELDS.has(k) && typeof v === 'number')
      target[k] = new Automerge.Uint(v)
    else if (INT_FIELDS.has(k) && typeof v === 'number')
      target[k] = new Automerge.Int(v)
  }
}

/**
 * Write / overwrite a tool worker in `thread.toolWorkers[id]` (RFC 0216).
 * Mirrors the entry-encoding rules: `name` and `currentToolUseId` are scalar
 * strings, `status` is an automorph unit-enum `{ status: RawString }`.
 */
export function writeToolWorker(
  thread: Record<string, unknown>,
  id: string,
  tw: {
    name: string
    status: string
    currentToolUseId?: string | null
    lastSeen?: number | null
    defaultModel?: string | null
  },
): void {
  const tws = thread.toolWorkers as Record<string, Record<string, unknown>>
  const out: Record<string, unknown> = {
    name: rs(tw.name),
    status: { status: rs(tw.status) },
  }
  if (tw.currentToolUseId != null)
    out.currentToolUseId = rs(tw.currentToolUseId)
  if (tw.defaultModel != null) out.defaultModel = rs(tw.defaultModel)
  tws[id] = out
  // lastSeen is an Int scalar (RFC 0240 liveness lease). Like the entry Int
  // fields it must be DIRECT-ASSIGNED after the wholesale object write — an
  // Automerge.Int nested in the literal above does not survive the assignment.
  if (tw.lastSeen != null)
    (tws[id] as Record<string, unknown>).lastSeen = new Automerge.Int(
      tw.lastSeen,
    )
}

/**
 * Re-encode the scalar metadata fields of an on-doc entry after an in-memory
 * mutation. Deliberately does NOT touch `text` or `toolUses` (streamed text and
 * the tool-use list are written via appendText / append, never re-set here).
 */
export function reencodeScalars(
  raw: { body: { payload: Record<string, unknown> } },
  decodedData: Record<string, unknown>,
): void {
  const target = raw.body.payload
  for (const [k, v] of Object.entries(decodedData)) {
    if (k === 'text' || k === 'toolUses') continue
    // Only scalar metadata is re-encoded here; array/object fields (inputHeads,
    // metadata, usage, model) are write-once at append and re-assigning their
    // decoded doc proxies would throw "reference to an existing document object".
    if (Array.isArray(v) || (v !== null && typeof v === 'object')) continue
    if (ENUM_FIELDS.has(k) && typeof v === 'string') target[k] = { [k]: rs(v) }
    else if (INT_FIELDS.has(k) && typeof v === 'number')
      target[k] = new Automerge.Int(v)
    else if (UINT_FIELDS.has(k) && typeof v === 'number')
      target[k] = new Automerge.Uint(v)
    // Scalar strings MUST be re-encoded as ImmutableString (matching encodeData);
    // a plain assignment becomes a collaborative Text, which breaks the
    // cross-peer automorph contract (e.g. `runId` read as Text, not String).
    else if (typeof v === 'string') target[k] = rs(v)
    else target[k] = v
  }
}

// ---------------------------------------------------------------------------
// decode (on-doc -> in-memory), for reading a loaded/synced document
// ---------------------------------------------------------------------------

function s(v: unknown): string {
  return String(v)
}

/** Fields whose values are genuine objects and must be kept as-is, not coerced. */
const OBJECT_FIELDS = new Set([
  'metadata',
  'usage',
  'arguments',
  'model',
  // Whiffletree ModelSelection objects ({ provider, model }); encodeJson already
  // scalarizes their inner strings, matching upstream's scalar fields.
  'requestedModel',
  'resolvedModel',
  'payload',
])

function decodeValue(k: string, v: unknown): unknown {
  if (
    ENUM_FIELDS.has(k) &&
    v &&
    typeof v === 'object' &&
    k in (v as Record<string, unknown>)
  ) {
    return s((v as Record<string, unknown>)[k])
  }
  if (typeof v === 'bigint') return Number(v)
  if (k === 'toolUses' && Array.isArray(v)) {
    return v.map((t) => {
      const tu = t as { id: unknown; name: unknown; arguments: unknown }
      return { id: s(tu.id), name: s(tu.name), arguments: tu.arguments }
    })
  }
  // RFC 0288: attachment pointers. Same hazard as `skills` below, one level
  // deeper: the generic array path returns the array as-is, so every field of
  // every ref stays an Automerge proxy. `docId` would then not compare equal to
  // the id it was written with, and would resolve to no document at all.
  if ((k === 'attachments' || k === 'viewAttachments') && Array.isArray(v)) {
    return v.map((a) => {
      const at = a as Record<string, unknown>
      return {
        docId: s(at.docId),
        name: s(at.name),
        mimeType: s(at.mimeType),
        bytes: Number(at.bytes),
        ...(at.width != null ? { width: Number(at.width) } : {}),
        ...(at.height != null ? { height: Number(at.height) } : {}),
      }
    })
  }
  // RFC 0283 S5: skill ids. Automerge hands back string-LIKE proxies inside an
  // array, and the generic array path below returns the array as-is, so without
  // this the decoded ids compare unequal to the ones written and match nothing
  // in the World's catalog.
  if (k === 'skills' && Array.isArray(v)) return v.map(s)
  if (OBJECT_FIELDS.has(k)) return v
  // Strings, and Automerge's string-like proxies for ids (typeof 'object' but
  // String()-able), both coerce to a primitive string.
  if (typeof v === 'string') return v
  if (v !== null && typeof v === 'object' && !Array.isArray(v)) return s(v)
  return v
}

function decodeData(raw: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(raw)) out[k] = decodeValue(k, v)
  return out
}

/** Decode one on-doc entry into the in-memory model. */
export function decodeEntry(raw: {
  id: unknown
  parents: unknown[]
  createdAt: unknown
  body: { type: unknown; payload: Record<string, unknown> }
}): Entry {
  return {
    id: s(raw.id),
    parents: (raw.parents ?? []).map(s),
    createdAt: Number(raw.createdAt),
    // In-memory Entry keeps `payload`; only the ON-DOC envelope is `body`.
    payload: {
      type: s(raw.body.type),
      payload: decodeData(raw.body.payload),
    },
  } as unknown as Entry
}

/**
 * Decoded roots, keyed by the doc object they came from.
 *
 * Automerge hands back a FRESH immutable object for every version of a
 * document, so the object identity is a version identity: a hit is the same
 * bytes by construction, and a doc that has changed simply misses. That makes
 * this a cache with no invalidation to get wrong, and a WeakMap means a doc
 * nobody holds any more takes its decode with it.
 *
 * It exists because decoding is O(entries) and the callers are not. A status
 * projection decodes the whole log twice (interactions, then runs), a worker
 * poll decodes it once per pending tool_use, and both run again on every sync
 * patch — so a large session log turned a list of twenty sessions into gigabytes
 * of short-lived garbage and pegged the drain loop at 100% of a core. Nothing
 * upstream asked for a different answer; they asked the same question again.
 */
const decoded = new WeakMap<object, LogDoc>()

/** Decode the whole on-doc root (`{ thread: {...} }`) into an in-memory LogDoc. */
export function decodeLogDoc(root: Record<string, unknown>): LogDoc {
  const hit = decoded.get(root)
  if (hit) return hit
  const out = decodeLogDocUncached(root)
  // FROZEN, SO ITS IDENTITY CAN STAND FOR ITS VERSION. A decoded log is one
  // object per Automerge version and is never edited in place; freezing the
  // container makes that a fact the projections can rely on (`topoOrder`
  // remembers its order only for a frozen `entries`), and makes any in-place
  // edit throw in strict mode rather than silently serve a stale projection.
  Object.freeze(out.entries)
  Object.freeze(out)
  decoded.set(root, out)
  return out
}

function decodeLogDocUncached(root: Record<string, unknown>): LogDoc {
  const thread = (root[THREAD_KEY] ?? {}) as {
    entries?: Record<string, unknown>
    toolWorkers?: Record<string, unknown>
  }
  const entries: Record<string, Entry> = {}
  for (const [id, raw] of Object.entries(thread.entries ?? {})) {
    entries[s(id)] = decodeEntry(raw as Parameters<typeof decodeEntry>[0])
  }
  const toolWorkers: LogDoc['toolWorkers'] = {}
  for (const [id, raw] of Object.entries(thread.toolWorkers ?? {})) {
    const r = raw as {
      name: unknown
      status: unknown
      currentToolUseId?: unknown
      lastSeen?: unknown
      defaultModel?: unknown
    }
    toolWorkers[s(id)] = {
      name: s(r.name),
      status: decodeValue(
        'status',
        r.status,
      ) as LogDoc['toolWorkers'][string]['status'],
      currentToolUseId:
        r.currentToolUseId != null ? s(r.currentToolUseId) : null,
      ...(r.lastSeen != null ? { lastSeen: Number(r.lastSeen) } : {}),
      ...(r.defaultModel != null ? { defaultModel: s(r.defaultModel) } : {}),
    }
  }
  return { schemaVersion: 1, entries, toolWorkers }
}
